import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType, VoiceCallEndReason } from 'analytics/events';
import { ConfigurationSettings } from 'models/configuration-settings';
import { useVoiceCallStore } from 'state/store/chat/use-voice-call-store';
import { buildChatConversationPath } from 'utils/chat-utilities';
import {
  invalidateAgentConversationList,
  prependAgentConversationToCache,
} from '../utils/agent-sidebar-sync';
import {
  classifyVoiceSessionError,
  createVoiceSession,
  endVoiceSession,
  endVoiceSessionKeepalive,
} from '../services/voice-call-service';
import {
  connectVoiceRoom,
  disconnectVoiceRoom,
  isVoiceRoomActive,
  requestMicPermission,
  setVoiceMicEnabled,
} from '../services/voice-call-room';
import { VoiceSession } from '../types/voice-call.types';
import { renderVoiceCallReconnectAction } from '../components/VoiceCallReconnectAction';

interface StartVoiceCallOptions {
  agentId: string;
  /** Continue this conversation by voice; omit to start a fresh one. */
  conversationId?: string;
}

const MIC_ERROR_NAMES = ['NotAllowedError', 'NotFoundError', 'NotReadableError'];
/** The worker normally joins within seconds; past this we give up instead of hanging on "Connecting…". */
export const AGENT_JOIN_TIMEOUT_MS = 45_000;

const isMicError = (error: unknown): boolean =>
  error instanceof Error && MIC_ERROR_NAMES.includes(error.name);

const callDurationSeconds = (startedAt: number | null): number =>
  startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

// Bumped per start attempt: a call torn down (or restarted) while its mic check or
// `POST /voice/session` was in flight must not resume and open a room afterwards.
let callAttempt = 0;
const isAttemptCurrent = (attempt: number): boolean =>
  attempt === callAttempt && useVoiceCallStore.getState().status === 'requesting';

// Registered once per page: a tab closed mid-call still frees the room server-side.
let unloadConfig: ConfigurationSettings | null = null;
let unloadBound = false;
const bindPageHide = (config: ConfigurationSettings) => {
  unloadConfig = config;
  if (unloadBound || typeof window === 'undefined') return;
  unloadBound = true;
  window.addEventListener('pagehide', () => {
    const { roomName } = useVoiceCallStore.getState();
    if (!isVoiceRoomActive() || !roomName || !unloadConfig) return;
    void disconnectVoiceRoom();
    endVoiceSessionKeepalive(unloadConfig, roomName);
  });
};

/**
 * Orchestrates a voice call: mic pre-flight → `POST /voice/session` → (fresh chat: open the
 * new conversation) → join the LiveKit room. ChatPage skips its full-page spinner while a
 * call is active, so /chat → /chat/:id does not look like a reload.
 */
export const useVoiceCall = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    bindPageHide(configurationSettings);
  }, [configurationSettings]);

  const endCall = useCallback(
    async (reason: VoiceCallEndReason) => {
      const state = useVoiceCallStore.getState();
      if (state.status === 'idle') return;
      state.setStatus('ending');
      await disconnectVoiceRoom();
      if (state.roomName) {
        endVoiceSession(configurationSettings, state.roomName).catch(() => undefined);
      }
      trackEvent(
        EVENTS.VOICE_CALL_ENDED(state.conversationId, callDurationSeconds(state.startedAt), reason)
      );
      useVoiceCallStore.getState().reset();
    },
    [configurationSettings]
  );

  const hangUp = useCallback(() => endCall('user'), [endCall]);

  // The Reconnect action is created inside startCall's own callbacks, so it reaches the
  // latest startCall through a ref instead of a circular dependency.
  const startCallRef = useRef<(options: StartVoiceCallOptions) => Promise<void>>();

  /** A call that ended without the user hanging up must say so and offer a way back. */
  const notifyCallEnded = useCallback(
    (options: StartVoiceCallOptions) => {
      const reconnectLabel = t('VOICE_CALL_RECONNECT', { defaultValue: 'Reconnect' });
      toast({
        title: t('VOICE_CALL_ENDED_TITLE', { defaultValue: 'Voice call ended' }),
        description: t('VOICE_CALL_DISCONNECTED', { defaultValue: 'The call was disconnected.' }),
        action: renderVoiceCallReconnectAction(
          reconnectLabel,
          () => void startCallRef.current?.(options)
        ),
      });
    },
    [t, toast]
  );

  const startCall = useCallback(
    async ({ agentId, conversationId }: StartVoiceCallOptions) => {
      const store = useVoiceCallStore.getState();
      if (store.status !== 'idle') return;
      const attempt = ++callAttempt;
      store.beginRequest(agentId);

      try {
        await requestMicPermission();
        if (!isAttemptCurrent(attempt)) return;
      } catch (error) {
        if (!isAttemptCurrent(attempt)) return;
        console.error('Microphone access denied or failed:', error);
        toast({
          variant: 'destructive',
          title: t('FAILED'),
          description: t('MICROPHONE_ACCESS_DENIED_OR_FAILED'),
        });
        useVoiceCallStore.getState().reset();
        return;
      }

      let session: VoiceSession;
      try {
        const response = await createVoiceSession(configurationSettings, {
          agentId,
          conversationId,
        });
        session = response.data;
      } catch (error) {
        if (!isAttemptCurrent(attempt)) return;
        const info = classifyVoiceSessionError(error);
        if (info.kind === 'unavailable') {
          useVoiceCallStore.getState().setUnavailable(true);
          toast({
            title: t('VOICE_CALL_UNAVAILABLE_TITLE', { defaultValue: 'Voice calls unavailable' }),
            description: t('VOICE_CALL_UNAVAILABLE', {
              defaultValue: "Voice calls aren't available here yet.",
            }),
          });
        } else {
          toast({
            variant: 'destructive',
            title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
            description:
              info.message ??
              t('VOICE_CALL_FAILED_GENERIC', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
          });
        }
        useVoiceCallStore.getState().reset();
        return;
      }

      if (!isAttemptCurrent(attempt)) {
        // The user moved on while the session was being created: free the room unused.
        endVoiceSession(configurationSettings, session.room).catch(() => undefined);
        return;
      }

      useVoiceCallStore
        .getState()
        .setSession({ conversationId: session.conversationId, roomName: session.room });
      trackEvent(EVENTS.VOICE_CALL_STARTED(session.conversationId, agentId));

      if (!conversationId) {
        // Fresh chat: the backend created the conversation without a WS event, so open it
        // now — the call's messages stream into it over the existing collaboration socket.
        trackEvent(EVENTS.NEW_CHAT_STARTED(session.conversationId, InitialPromptType.VOICE));
        prependAgentConversationToCache(
          queryClient,
          agentId,
          configurationSettings.blocksKey || '',
          session.conversationId
        );
        invalidateAgentConversationList(queryClient);
        navigate(buildChatConversationPath(session.conversationId, { agentId }));
      }

      let joinTimer: ReturnType<typeof setTimeout> | undefined;
      const clearJoinTimer = () => {
        if (joinTimer) clearTimeout(joinTimer);
        joinTimer = undefined;
      };
      try {
        await connectVoiceRoom(session, {
          onAgentJoined: () => {
            clearJoinTimer();
            useVoiceCallStore.getState().setStatus('in-call');
          },
          onAgentLeft: () => {
            console.warn('Voice call: the agent left the room');
            const { status, conversationId: activeConversationId } = useVoiceCallStore.getState();
            const wasLive = status === 'in-call' || status === 'reconnecting';
            void endCall('agent_left');
            if (wasLive && activeConversationId) {
              notifyCallEnded({ agentId, conversationId: activeConversationId });
            }
          },
          onReconnecting: () => {
            if (useVoiceCallStore.getState().status !== 'in-call') return;
            console.warn('Voice call: connection lost, reconnecting');
            useVoiceCallStore.getState().setStatus('reconnecting');
          },
          onReconnected: () => {
            if (useVoiceCallStore.getState().status !== 'reconnecting') return;
            useVoiceCallStore.getState().setStatus('in-call');
          },
          onAgentState: (agentState) => useVoiceCallStore.getState().setAgentState(agentState),
          onCaption: (caption) => useVoiceCallStore.getState().upsertCaption(caption),
          onMediaDeviceError: (error) => {
            console.error('Voice call media device error:', error);
            toast({
              variant: 'destructive',
              title: t('FAILED'),
              description: t('MICROPHONE_ACCESS_DENIED_OR_FAILED'),
            });
          },
          onDisconnected: (reason) => {
            clearJoinTimer();
            const state = useVoiceCallStore.getState();
            if (state.status === 'idle' || state.status === 'ending') return;
            console.warn('Voice call: room disconnected', { reason, status: state.status });
            if (state.status === 'connecting') {
              toast({
                variant: 'destructive',
                title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
                description: t('VOICE_CALL_DISCONNECTED', {
                  defaultValue: 'The call was disconnected.',
                }),
              });
            }
            trackEvent(
              EVENTS.VOICE_CALL_ENDED(
                state.conversationId,
                callDurationSeconds(state.startedAt),
                'disconnected'
              )
            );
            const wasLive = state.status === 'in-call' || state.status === 'reconnecting';
            const endedConversationId = state.conversationId;
            state.reset();
            if (wasLive && endedConversationId) {
              notifyCallEnded({ agentId, conversationId: endedConversationId });
            }
          },
        });
        if (useVoiceCallStore.getState().status === 'connecting') {
          joinTimer = setTimeout(() => {
            if (useVoiceCallStore.getState().status !== 'connecting') return;
            toast({
              variant: 'destructive',
              title: t('VOICE_CALL_FAILED', { defaultValue: 'Could not start the call' }),
              description: t('VOICE_CALL_AGENT_NOT_JOINED', {
                defaultValue: 'The agent did not answer. Please try again.',
              }),
            });
            void endCall('error');
          }, AGENT_JOIN_TIMEOUT_MS);
        }
      } catch (error) {
        clearJoinTimer();
        console.error('Voice call connect failed:', error);
        toast({
          variant: 'destructive',
          title: t('FAILED'),
          description: isMicError(error)
            ? t('MICROPHONE_ACCESS_DENIED_OR_FAILED')
            : t('VOICE_CALL_FAILED_GENERIC', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
        });
        await disconnectVoiceRoom();
        endVoiceSession(configurationSettings, session.room).catch(() => undefined);
        useVoiceCallStore.getState().reset();
      }
    },
    [configurationSettings, endCall, navigate, notifyCallEnded, queryClient, t, toast]
  );
  startCallRef.current = startCall;

  const toggleMute = useCallback(async () => {
    const next = !useVoiceCallStore.getState().isMuted;
    await setVoiceMicEnabled(!next);
    useVoiceCallStore.getState().setMuted(next);
  }, []);

  return { startCall, hangUp, toggleMute, endCall };
};

/**
 * Ends the call when the chat it belongs to is no longer on screen: New Chat, another
 * conversation, or another agent. Leaving chat entirely (settings, library) keeps the call —
 * ChatPage unmounts, so this does not run. A fresh-chat call navigates `/chat` →
 * `/chat/<its conversation>` itself; that target matches the call and is kept.
 */
export const useEndVoiceCallOnChatChange = (
  conversationId: string | undefined,
  agentIdFromUrl: string | null,
  endCall: (reason: VoiceCallEndReason) => Promise<void>
) => {
  const targetRef = useRef<string | null>(null);

  useEffect(() => {
    const target = `${conversationId ?? ''}|${agentIdFromUrl ?? ''}`;
    const previousTarget = targetRef.current;
    targetRef.current = target;
    const state = useVoiceCallStore.getState();
    if (state.status === 'idle' || state.status === 'ending') return;

    if (state.conversationId) {
      if (state.conversationId !== conversationId) void endCall('navigation');
      return;
    }
    // Still requesting (no conversation yet): only an actual route change means the user
    // left the chat they started the call from.
    if (previousTarget !== null && previousTarget !== target) void endCall('navigation');
  }, [conversationId, agentIdFromUrl, endCall]);
};
